import {
  BrandingWatermarkOutlined,
  DateRangeOutlined,
  LiveTvOutlined,
  MmsOutlined,
  PermMediaOutlined,
  PlaylistPlayOutlined,
  SubscriptionsOutlined,
  TvOutlined,
  ViewModule,
} from "@mui/icons-material";
import AddBusinessOutlinedIcon from "@mui/icons-material/AddBusinessOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import OndemandVideoOutlinedIcon from "@mui/icons-material/OndemandVideoOutlined";
import AddCardIcon from "@mui/icons-material/AddCard";
import { capitalize } from "@mui/material";
import {
  Company,
  IOrganizationUnit,
  NavigationLevel,
  NendaProduct,
  Resource,
  Restriction,
  Role,
  Scope,
} from "../../../../types/NendaTypes";
import i18next from "../i18n";

export type MenuItem = {
  id: string;
  title: string;
  caption?: string;
  type: string;
  children?: MenuItem[];
  url?: string;
  icon?: any;
  chip?: any;
  breadcrumbs?: boolean;
  target?: boolean;
  external?: boolean;
  disabled?: boolean;
  restriction?: Restriction;
  products?: NendaProduct[];
  level?: NavigationLevel[];
};

export const dynamicMenu = ({
  scope,
  role,
  navigationLevel,
}: {
  scope: {
    premise: IOrganizationUnit | undefined;
    scopedPremises?: IOrganizationUnit[];
    company: Company | undefined;
  };
  role: Role | undefined;
  navigationLevel?: NavigationLevel;
}) => {
  const isGlobalAdmin = role
    ? [Role.Admin, Role.Superuser].includes(role)
    : false;
  const companyId = scope.company?._id;
  const premiseId = scope.premise?._id;
  const shouldShowPromotionsOnPremiseLevel =
    scope.premise?.signageConfig?.arePromotionsEnabled &&
    scope.premise?.nendaProducts?.includes(NendaProduct.SIGNAGE);
  const shouldShowPromotionsOnCompanyLevel = scope.scopedPremises?.some(
    (p) =>
      p.signageConfig?.arePromotionsEnabled &&
      p.nendaProducts?.includes(NendaProduct.SIGNAGE)
  );
  const shouldShowDigitalSignageOnPremiseLevel =
    scope.premise?.signageConfig?.isSignageAsContentEnabled &&
    scope.premise?.nendaProducts?.includes(NendaProduct.SIGNAGE);
  const shouldShowDigitalSignageOnCompanyLevel = scope.scopedPremises?.some(
    (p) =>
      p.signageConfig?.isSignageAsContentEnabled &&
      p.nendaProducts?.includes(NendaProduct.SIGNAGE)
  );
  const showPromotions =
    navigationLevel === NavigationLevel.PREMISE
      ? shouldShowPromotionsOnPremiseLevel
      : shouldShowPromotionsOnCompanyLevel;

  const showDigitalSignage =
    navigationLevel === NavigationLevel.PREMISE
      ? shouldShowDigitalSignageOnPremiseLevel
      : shouldShowDigitalSignageOnCompanyLevel;

  const showScheduling =
    navigationLevel === NavigationLevel.PREMISE
      ? true
      : scope.company?.canManagePremises;

  const shouldShowGroups = scope.company?.canManagePremises;

  const adminMenu: MenuItem = {
    id: "admin",
    title: "Menu",
    type: "group",
    children: [
      {
        id: "premiselist",
        title: i18next.t("customerportal.sidemenu.premises"),
        type: "item",
        url: `admin/premiselist`,
        icon: AddBusinessOutlinedIcon,
        restriction: {
          resource: Resource.Company,
          scopes: [Scope.CanAdministrate],
        },
        breadcrumbs: false,
      },
      {
        id: "companies",
        title: i18next.t("customerportal.sidemenu.companies"),
        type: "item",
        url: "admin/companies",
        icon: BusinessOutlinedIcon,
        breadcrumbs: false,
        restriction: {
          resource: Resource.Company,
          scopes: [Scope.CanAdministrate],
        },
      },
      // {
      //   id: "content",
      //   title: i18next.t("customerportal.sidemenu.effect_content"),
      //   type: "item",
      //   url: "admin/content",
      //   icon: OndemandVideoOutlinedIcon,
      //   breadcrumbs: false,
      //   restriction: {
      //     resource: Resource.Ads,
      //     scopes: [Scope.CanAdministrate],
      //   },
      // },
      {
        id: "metadata",
        title: i18next.t("customerportal.sidemenu.content.content"),
        type: "collapse",
        url: "",
        icon: OndemandVideoOutlinedIcon,
        breadcrumbs: false,
        restriction: {
          resource: Resource.Content,
          scopes: [Scope.CanAdministrate],
        },
        children: [
          {
            id: "effect-channels",
            title: i18next.t("customerportal.sidemenu.content.effect_channels"),
            type: "item",
            url: "admin/metadata/effect-channels",
            icon: OndemandVideoOutlinedIcon,
            breadcrumbs: false,
            restriction: {
              resource: Resource.ContentChannel,
              scopes: [Scope.CanAdministrate],
            },
          },
          {
            id: "vod",
            title: i18next.t("customerportal.sidemenu.content.vod"),
            type: "item",
            url: "admin/metadata/vod",
            icon: OndemandVideoOutlinedIcon,
            breadcrumbs: false,
            restriction: {
              resource: Resource.Content,
              scopes: [Scope.CanAdministrate],
            },
          },
          {
            id: "live-channels",
            title: i18next.t("customerportal.sidemenu.content.live_channels"),
            type: "item",
            url: "admin/metadata/live-channels",
            icon: OndemandVideoOutlinedIcon,
            breadcrumbs: false,
            restriction: {
              resource: Resource.Content,
              scopes: [Scope.CanAdministrate],
            },
          },
          {
            id: "tags",
            title: i18next.t("customerportal.sidemenu.content.tags"),
            type: "item",
            url: "admin/metadata/tags",
            icon: OndemandVideoOutlinedIcon,
            breadcrumbs: false,
            restriction: {
              resource: Resource.Content,
              scopes: [Scope.CanAdministrate],
            },
          },
          {
            id: "bulk-ingest",
            title: "Bulk Ingest",
            type: "item",
            url: "admin/metadata/bulk-ingest",
            icon: OndemandVideoOutlinedIcon,
            breadcrumbs: false,
            restriction: {
              resource: Resource.Content,
              scopes: [Scope.CanAdministrate],
            },
          },
        ],
      },
      {
        id: "offerings",
        title: i18next.t("customerportal.sidemenu.offerings.offerings"),
        type: "collapse",
        url: "",
        icon: AddCardIcon,
        breadcrumbs: false,
        restriction: {
          resource: Resource.Content,
          scopes: [Scope.CanAdministrate],
        },
        children: [
          {
            id: "feature-sets",
            title: i18next.t("customerportal.sidemenu.offerings.feature_sets"),
            type: "item",
            url: "admin/offerings/feature-sets",
            icon: AddCardIcon,
            breadcrumbs: false,
            restriction: {
              resource: Resource.Content,
              scopes: [Scope.CanAdministrate],
            },
          },
          {
            id: "media-collections",
            title: i18next.t(
              "customerportal.sidemenu.content.media_collections"
            ),
            type: "item",
            url: "admin/offerings/media-collections",
            icon: OndemandVideoOutlinedIcon,
            breadcrumbs: false,
            restriction: {
              resource: Resource.Content,
              scopes: [Scope.CanAdministrate],
            },
          },
        ],
      },
      {
        id: "users",
        title: i18next.t("customerportal.sidemenu.users"),
        type: "item",
        url: `/users`,
        icon: GroupOutlinedIcon,
        breadcrumbs: false,
        restriction: {
          resource: Resource.User,
          scopes: [Scope.CanAdministrate],
        },
      },
    ].filter(Boolean) as MenuItem[],
  };

  const mainMenu: MenuItem = {
    id: "main",
    title: "Menu",
    type: "group",
    children: [
      {
        id: "dashboard",
        title: i18next.t("customerportal.sidemenu.dashboard"),
        type: "item",
        url: premiseId
          ? `/dashboard/premise/${premiseId}`
          : companyId
            ? `/dashboard/company/${companyId}`
            : "/",
        icon: DashboardOutlinedIcon,
        level: [NavigationLevel.PREMISE, NavigationLevel.COMPANY],
        breadcrumbs: false,
      },
      showScheduling
        ? {
            id: "scheduling",
            title: i18next.t("customerportal.sidemenu.scheduling"),
            type: "collapse",
            url:
              navigationLevel === NavigationLevel.PREMISE
                ? `/scheduling/schedule/premise/${premiseId}`
                : `/scheduling/schedule/company/${companyId}`,
            icon: DateRangeOutlined,
            products: [NendaProduct.SIGNAGE],
            level: [NavigationLevel.PREMISE, NavigationLevel.COMPANY],
            restriction: {
              resource: Resource.Premise,
              scopes: [Scope.CanView],
            },
            children: [
              {
                id: "schedule",
                title: i18next.t("customerportal.sidemenu.schedule"),
                type: "item",
                url:
                  navigationLevel === NavigationLevel.PREMISE
                    ? `/scheduling/schedule/premise/${premiseId}`
                    : `/scheduling/schedule/company/${companyId}`,
                icon: DateRangeOutlined,
                restriction: {
                  resource: Resource.Premise,
                  scopes: [Scope.CanView],
                },
                breadcrumbs: false,
              },
              {
                id: "daily-playlists",
                title: i18next.t("customerportal.sidemenu.daily_playlists"),
                type: "item",
                url:
                  navigationLevel === NavigationLevel.PREMISE
                    ? `/scheduling/daily-playlists/premise/${premiseId}`
                    : `/scheduling/daily-playlists/company/${companyId}`,
                icon: PlaylistPlayOutlined,
                restriction: {
                  resource: Resource.Premise,
                  scopes: [Scope.CanView],
                },
                breadcrumbs: false,
              },
            ],
          }
        : undefined,
      showScheduling
        ? {
            id: "channels",
            title: i18next.t("customerportal.sidemenu.channels"),
            type: "item",
            icon: LiveTvOutlined,
            url:
              navigationLevel === NavigationLevel.PREMISE
                ? `/channels/premise/${premiseId}`
                : `/channels/company/${companyId}`,
            products: [NendaProduct.SIGNAGE],
            restriction: {
              resource: Resource.Premise,
              scopes: [Scope.CanView],
            },
            level: [NavigationLevel.PREMISE, NavigationLevel.COMPANY],
            breadcrumbs: false,
          }
        : undefined,
      showDigitalSignage
        ? {
            id: "digital-signage",
            title: i18next.t("customerportal.sidemenu.digital_signage"),
            type: "item",
            url: `/${
              premiseId ? "premise/" + premiseId : "company/" + companyId
            }/digital-signage-playlists`,
            icon: SubscriptionsOutlined,
            restriction: {
              resource: Resource.SignagePlaylist,
              scopes: [Scope.CanView],
            },
            products: [NendaProduct.SIGNAGE],
            level: [NavigationLevel.PREMISE, NavigationLevel.COMPANY],
            breadcrumbs: false,
          }
        : undefined,
      showPromotions
        ? {
            id: "promotions",
            title: i18next.t("customerportal.sidemenu.promotions"),
            type: "item",
            icon: MmsOutlined,
            url: `/promotions/${
              premiseId ? "premise/" + premiseId : "company/" + companyId
            }`,
            restriction: {
              resource: Resource.SignagePromotion,
              scopes: [Scope.CanView],
            },
            breadcrumbs: false,
          }
        : undefined,
      {
        id: "frontlayer",
        title: i18next.t("customerportal.sidemenu.frontlayer_settings"),
        type: "item",
        url: `/frontlayer/premise/${premiseId}`,
        products: [NendaProduct.FRONTLAYER],
        level: [NavigationLevel.PREMISE],
        icon: BrandingWatermarkOutlined,
        breadcrumbs: false,
        restriction: {
          resource: Resource.Premise,
          scopes: [Scope.CanEdit],
        },
      },
      {
        id: "premises",
        title: i18next.t("customerportal.sidemenu.premises"),
        type: "item",
        level: [NavigationLevel.COMPANY],
        url: premiseId
          ? `/premises/premise/${premiseId}`
          : `/premises/company/${companyId}`,
        icon: AddBusinessOutlinedIcon,
        restriction: {
          resource: Resource.Company,
          scopes: [Scope.CanEdit],
        },
        breadcrumbs: false,
      },
      {
        id: "media-library",
        title: i18next.t("customerportal.sidemenu.assets"),
        type: "item",
        icon: PermMediaOutlined,
        url: `/media-library/${
          premiseId ? "premise/" + premiseId : "company/" + companyId
        }`,
        restriction: { resource: Resource.Asset, scopes: [Scope.CanView] },
        products: [NendaProduct.SIGNAGE],
        breadcrumbs: false,
      },
      {
        id: "units",
        title: capitalize(i18next.t("glossary.unit", { count: 2 })),
        type: "item",
        url: `/premise/${premiseId}/units`,
        icon: TvOutlined,
        level: [NavigationLevel.PREMISE],
        restriction: {
          resource: Resource.Premise,
          scopes: [Scope.CanAdministrate],
        },
      },
      shouldShowGroups && {
        id: "groups",
        title: capitalize(i18next.t("glossary.group", { count: 2 })),
        type: "item",
        url: `/${
          premiseId ? "premise/" + premiseId : "company/" + companyId
        }/groups`,
        icon: ViewModule,
        level: [NavigationLevel.PREMISE, NavigationLevel.COMPANY],
        restriction: {
          resource: Resource.Group,
          scopes: [Scope.CanEdit],
        },
      },
    ].filter(Boolean) as MenuItem[],
  };

  return isGlobalAdmin && !companyId && !premiseId ? [adminMenu] : [mainMenu];
};
